import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import UnifiedModal from '../common/UnifiedModal';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { createLesson } from '../../../store/slices/lessonSlice';
import { fetchStudents } from '../../../store/slices/userSlice';

const Form = styled.form`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.25rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div<{ $fullWidth?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  ${({ $fullWidth }) => $fullWidth && 'grid-column: 1 / -1;'}
`;

const Label = styled.label`
  font-weight: 600;
  font-size: 0.9rem;
  color: var(--text-dark, #374151);
`;

const Input = styled.input`
  padding: 0.75rem;
  border-radius: 8px;
  border: 1px solid var(--border-light, #e5e7eb);
  background: var(--input-bg, #fff);
  color: var(--text, #111827);
  font-size: 1rem;

  &:focus {
    outline: none;
    border-color: var(--primary-blue, #667eea);
    box-shadow: 0 0 0 2px rgba(102, 126, 234, 0.1);
  }
`;

const Select = styled.select`
  padding: 0.75rem;
  border-radius: 8px;
  border: 1px solid var(--border-light, #e5e7eb);
  background: var(--input-bg, #fff);
  color: var(--text, #111827);
  font-size: 1rem;

  &:focus {
    outline: none;
    border-color: var(--primary-blue, #667eea);
    box-shadow: 0 0 0 2px rgba(102, 126, 234, 0.1);
  }
`;

const Button = styled.button<{ $variant?: 'primary' | 'secondary' }>`
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s;
  border: none;

  ${({ $variant }) =>
    $variant === 'secondary'
      ? `
    background: var(--border-light, #e5e7eb);
    color: var(--text-dark, #374151);
    &:hover { background: #d1d5db; }
  `
      : `
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    &:hover { filter: brightness(1.1); }
    &:disabled { opacity: 0.6; cursor: not-allowed; }
  `}
`;

const ErrorMsg = styled.div`
  color: #ef4444;
  font-size: 0.85rem;
  margin-top: 0.25rem;
`;

interface LessonCreateModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  initialDate?: string; // YYYY-MM-DD
}

const LessonCreateModal: React.FC<LessonCreateModalProps> = ({ open, onClose, onSuccess, initialDate }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { user } = useAppSelector(s => s.auth);
  const { students } = useAppSelector(s => s.user || { students: [] });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const formatDateTimeLocal = (date: Date) => {
    const pad = (n: number) => n.toString().padStart(2, '0');
    const y = date.getFullYear();
    const m = pad(date.getMonth() + 1);
    const d = pad(date.getDate());
    const h = pad(date.getHours());
    const min = pad(date.getMinutes());
    return `${y}-${m}-${d}T${h}:${min}`;
  };

  const [dateTime, setDateTime] = useState('');
  const [durationMinutes, setDurationMinutes] = useState(60);
  const [type, setType] = useState('REGULAR');
  const [studentId, setStudentId] = useState('');

  useEffect(() => {
    if (open) {
      if (!students || students.length === 0) {
        dispatch(fetchStudents());
      }
      let start: Date;
      if (initialDate) {
        const [y, m, d] = initialDate.split('-').map(Number);
        // Default to 09:00 on the selected day
        start = new Date(y, m - 1, d, 9, 0);
      } else {
        const now = new Date();
        start = new Date(now.getFullYear(), now.getMonth(), now.getDate(), now.getHours() + 1, 0);
      }
      setDateTime(formatDateTimeLocal(start));
      setDurationMinutes(60);
      setType('REGULAR');
      setStudentId('');
      setError(null);
    }
  }, [open, initialDate, dispatch]);

  const myStudents = React.useMemo(() => {
    const meId = user?.id != null ? String(user.id) : '';
    return (students || []).filter((s: any) => String(s.assignedTeacher) === meId || String(s.assignedTeacherId) === meId);
  }, [students, user]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    setLoading(true);
    try {
      await dispatch(createLesson({
        dateTime,
        duration: durationMinutes,
        type,
        studentId: studentId || null,
        status: studentId ? 'SCHEDULED' : 'AVAILABLE'
      } as any)).unwrap();
      onSuccess?.();
      onClose();
    } catch (err: any) {
      // Backend error messages might be in English and specific. 
      // If we have a translation for the generic error, we use it only as fallback.
      const backendMessage = err?.message || (typeof err === 'string' ? err : null);
      setError(backendMessage || t('portal.lessons.create.error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <UnifiedModal
      open={open}
      onClose={onClose}
      title={String(t('portal.lessons.create.title'))}
      variant="center"
      size="lg"
    >
      <Form onSubmit={handleSubmit}>
        <FormGroup>
          <Label>{t('portal.lessons.create.date_time')}</Label>
          <Input
            type="datetime-local"
            value={dateTime}
            onChange={(e) => setDateTime(e.target.value)}
            required
          />
        </FormGroup>

        <FormGroup>
          <Label>{t('portal.lessons.create.duration')}</Label>
          <Input
            type="number"
            min="15"
            step="15"
            value={durationMinutes}
            onChange={(e) => setDurationMinutes(parseInt(e.target.value))}
            required
          />
        </FormGroup>

        <FormGroup>
          <Label>{t('portal.lessons.create.type')}</Label>
          <Select value={type} onChange={(e) => setType(e.target.value)}>
            <option value="REGULAR">Regular</option>
            <option value="TRIAL">Trial</option>
            <option value="EXAM">Exam</option>
          </Select>
        </FormGroup>


        <FormGroup>
          <Label>Student (Optional)</Label>
          <Select value={studentId} onChange={(e) => setStudentId(e.target.value)}>
            <option value="">-- No student --</option>
            {myStudents.map(s => (
              <option key={s.id} value={s.id}>
                {s.firstName} {s.lastName}
              </option>
            ))}
          </Select>
        </FormGroup>

        {error && <FormGroup $fullWidth><ErrorMsg>{error}</ErrorMsg></FormGroup>}

        <div style={{ display: 'flex', gap: '1rem', marginTop: '1rem', gridColumn: '1 / -1' }}>
          <Button type="button" $variant="secondary" onClick={onClose} style={{ flex: 1 }}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" disabled={loading} style={{ flex: 2 }}>
            {loading ? t('common.loading') : t('portal.lessons.create.submit')}
          </Button>
        </div>
      </Form>
    </UnifiedModal>
  );
};

export default LessonCreateModal;
